import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db, auth } from "../firebase";
import cache from "../utils/cache";
import { signOut } from "firebase/auth";
import { useAuth } from "../context/AuthContext";

const menuItems = [
  "Home",
  "Today's Deals",
  "Bank Offers",
  "Freebies",
  "Weekly Ads",
  "Vendors",
  "Contact Us",
  "Help",
];

const specialOfferColors = [
  "bg-gradient-to-r from-red-500 to-pink-500 text-white",
  "bg-gradient-to-r from-yellow-400 to-orange-500 text-black",
  "bg-gradient-to-r from-green-400 to-emerald-600 text-white",
  "bg-gradient-to-r from-blue-400 to-indigo-600 text-white",
  "bg-gradient-to-r from-pink-500 to-fuchsia-600 text-white",
];

export default function Navbar() {
  const [activeMenu, setActiveMenu] = useState("Home");
  const [specialOffers, setSpecialOffers] = useState(cache.specialOffers || []);
  const [newsItems, setNewsItems] = useState(cache.specialNews || []);
  const [currentNewsIndex, setCurrentNewsIndex] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();

  const { currentUser, firstName, loading } = useAuth();
  const email = currentUser?.email || "";

  // Track active menu
  useEffect(() => {
    const path = location.pathname.toLowerCase();
    if (path === "/" || path === "") setActiveMenu("Home");
    else if (path.includes("contact")) setActiveMenu("Contact Us");
    else if (path.includes("bank")) setActiveMenu("Bank Offers");
    else if (path.includes("weekly")) setActiveMenu("Weekly Ads");
    else if (path.includes("vendors")) setActiveMenu("Vendors");
    else if (path.includes("free-products")) setActiveMenu("Freebies");
    else if (path.includes("help")) setActiveMenu("Help");
    else if (path.includes("special-offers")) setActiveMenu("Special Offers");
    else if (path.includes("todays-deals")) setActiveMenu("Today's Deals");
    else setActiveMenu("Home");
  }, [location]);

  // Load special offers
  useEffect(() => {
    if (cache.specialOffers?.length) {
      setSpecialOffers(cache.specialOffers);
      return;
    }
    (async () => {
      try {
        const snapshot = await getDocs(collection(db, "special_offer"));
        let offers = [];
        const now = new Date();

        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          if (Array.isArray(data.offers)) {
            const valid = data.offers.filter((offer) => {
              if (!offer.offerName) return false;
              if (!offer.startDttm || !offer.endDttm) return true;

              const s = offer.startDttm?.toDate
                ? offer.startDttm.toDate()
                : new Date(offer.startDttm);
              const e = offer.endDttm?.toDate
                ? offer.endDttm.toDate()
                : new Date(offer.endDttm);

              return now >= s && now <= e;
            });
            offers = offers.concat(valid);
          }
        });

        offers.sort((a, b) => {
          const aStart = a.startDttm?.toDate
            ? a.startDttm.toDate()
            : new Date(a.startDttm || 0);
          const bStart = b.startDttm?.toDate
            ? b.startDttm.toDate()
            : new Date(b.startDttm || 0);
          return bStart - aStart;
        });

        cache.specialOffers = offers;
        setSpecialOffers(offers);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  // Load special_news
  useEffect(() => {
    if (cache.specialNews?.length) {
      setNewsItems(cache.specialNews);
      return;
    }

    (async () => {
      try {
        const snapshot = await getDocs(collection(db, "special_news"));
        let newsList = [];
        const now = new Date();

        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          if (Array.isArray(data.news)) {
            const valid = data.news.filter((n) => {
              if (!n.text) return false;

              const s = n.startDttm?.toDate
                ? n.startDttm.toDate()
                : new Date(n.startDttm);
              const e = n.endDttm?.toDate
                ? n.endDttm.toDate()
                : new Date(n.endDttm);

              return now >= s && now <= e;
            });

            newsList = newsList.concat(valid);
          }
        });

        newsList.sort((a, b) => {
          const aStart = a.startDttm?.toDate
            ? a.startDttm.toDate()
            : new Date(a.startDttm || 0);
          const bStart = b.startDttm?.toDate
            ? b.startDttm.toDate()
            : new Date(b.startDttm || 0);
          return bStart - aStart;
        });

        cache.specialNews = newsList;
        setNewsItems(newsList);
      } catch (err) {
        console.error("Error loading news:", err);
      }
    })();
  }, []);

  // Auto-change news every 3 seconds
  useEffect(() => {
    if (newsItems.length === 0) return;

    const timer = setInterval(() => {
      setCurrentNewsIndex((prev) => (prev + 1) % newsItems.length);
    }, 3000);

    return () => clearInterval(timer);
  }, [newsItems]);

  const handleMenuClick = (item) => {
    switch (item) {
      case "Home":
        sessionStorage.removeItem("vendorDetailState");
        navigate("/");
        break;
      case "Today's Deals":
        navigate("/todays-deals");
        break;
      case "Contact Us":
        navigate("/contact");
        break;
      case "Bank Offers":
        navigate("/bank-offers");
        break;
      case "Freebies":
        navigate("/free-products");
        break;
      case "Weekly Ads":
        navigate("/weekly-ads");
        break;
      case "Vendors":
        navigate("/vendors");
        break;
      case "Help":
        navigate("/help");
        break;
      default:
        navigate("/");
    }
  };

  return (
    <>
      {/* ⭐ TOP NEWS BANNER — STICKY ROW */}
      {newsItems.length > 0 && (
        <div className="w-full bg-blue-200 border-b border-blue-300 py-1 flex items-center justify-center sticky top-0 z-[60]">

          <div className="
              text-blue-900 
              text-[13px] sm:text-sm lg:text-base
              font-semibold 
              px-4 
              transition-all duration-500 
              w-full
              flex justify-center items-center gap-2
              text-center
              overflow-hidden 
              text-ellipsis
            ">

            {/* Rotating icon */}
            {(() => {
              const icons = ["📢", "🔔", "📰", "🛒", "🔥", "⭐", "💥", "🆕"];
              const icon = icons[currentNewsIndex % icons.length];
              return (
                <span className="text-[16px] sm:text-[18px] lg:text-[20px]">
                  {icon}
                </span>
              );
            })()}

            <button
              onClick={() => navigate(newsItems[currentNewsIndex].url)}
              className="underline hover:text-blue-600"
            >
              {newsItems[currentNewsIndex].text}
            </button>


          </div>
        </div>
      )}

      {/* ⭐ MAIN NAVBAR — SHIFTED DOWN BY BANNER HEIGHT */}
      <nav className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-0 bg-white/90 backdrop-blur-md py-3 px-2 shadow-sm border-b border-gray-100 sticky top-[28px] z-[50]">
        {menuItems
          .filter((item) => item === "Home")
          .map((item) => (
            <button
              key={item}
              onClick={() => handleMenuClick(item)}
              className={`px-3 py-1.5 text-sm sm:px-4 sm:py-2 sm:text-base font-semibold rounded-full transition-all duration-300 shadow-sm ${activeMenu === item
                ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md scale-105"
                : "bg-gray-100 text-gray-700 hover:bg-indigo-500 hover:text-white hover:scale-105"
                }`}
            >
              {item}
            </button>
          ))}

        {specialOffers.length > 0 &&
          specialOffers.map((offer, idx) => {
            const colorClass = specialOfferColors[idx % specialOfferColors.length];
            return (
              <button
                key={`special-offer-${idx}`}
                onClick={() =>
                  navigate(`/special-offers/${encodeURIComponent(offer.offerName)}`)
                }
                className={`${colorClass} animate-pulse px-3 py-1 sm:px-4 sm:py-1.5 text-sm sm:text-base font-semibold rounded-full shadow-sm hover:opacity-90 hover:scale-105 transition-transform duration-300`}
              >
                {offer.offerName}
              </button>
            );
          })}

        {menuItems
          .filter((item) => item !== "Home")
          .map((item) => (
            <button
              key={item}
              onClick={() => handleMenuClick(item)}
              className={`relative flex items-center gap-1 px-2.5 py-1 text-xs sm:px-3 sm:py-1.5 sm:text-sm font-medium rounded-full transition-all duration-300 shadow-sm ${activeMenu === item
                ? item === "Today's Deals"
                  ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md scale-105"
                  : "bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-md scale-105"
                : item === "Today's Deals"
                  ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:opacity-90 hover:scale-105"
                  : "bg-gray-100 text-gray-700 hover:bg-indigo-500 hover:text-white hover:scale-105"
                }`}
            >
              {item}
              {item === "Today's Deals" && (
                <span className="absolute -top-1 -right-2 px-1 text-[9px] font-bold rounded-full bg-yellow-400 text-black">
                  NEW
                </span>
              )}
            </button>
          ))}
      </nav>

      {/* ⭐ WELCOME BAR */}
      <div className="w-full bg-gray-50 border-b border-gray-200 py-2 shadow-sm">
        {loading ? (
          <div className="text-center text-gray-500 text-sm animate-pulse">
            Loading user...
          </div>
        ) : currentUser ? (
          <div className="flex flex-wrap justify-center items-center gap-3 text-sm sm:text-[15px] py-1">
            <span className="text-gray-700">
              👋 <span className="font-semibold text-gray-800">Welcome back,</span>
            </span>

            <span
              className={`text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-purple-600 font-bold transition-opacity duration-300 ${firstName ? "opacity-100" : "opacity-50"
                }`}
            >
              {email === "ymmvdeal@gmail.com"
                ? "Admin"
                : firstName || "Loading..."}
            </span>

            <button
              onClick={() => navigate("/profile")}
              className="border border-indigo-500 text-indigo-600 hover:bg-indigo-50 px-2.5 py-[3px] rounded-full text-xs sm:text-sm transition-all duration-200 shadow-sm"
            >
              Profile
            </button>

            <button
              onClick={async () => await signOut(auth)}
              className="border border-red-500 text-red-600 hover:bg-red-50 px-2.5 py-[3px] rounded-full text-xs sm:text-sm transition-all duration-200 shadow-sm"
            >
              Logout
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap justify-center items-center gap-3 text-sm sm:text-[15px] py-1">
            <span className="text-gray-700">
              👋 <span className="font-semibold text-gray-800">Welcome,</span>
            </span>

            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-purple-600 font-bold">
              Guest
            </span>

            <button
              onClick={() => navigate("/login")}
              className="border border-indigo-500 text-indigo-600 hover:bg-indigo-50 px-2.5 py-[3px] rounded-full text-xs sm:text-sm transition-all duration-200 shadow-sm"
            >
              Login
            </button>

            <span className="text-gray-400">or</span>

            <button
              onClick={() => navigate("/signup")}
              className="border border-indigo-500 text-indigo-600 hover:bg-indigo-50 px-2.5 py-[3px] rounded-full text-xs sm:text-sm transition-all duration-200 shadow-sm"
            >
              Sign Up
            </button>
          </div>
        )}
      </div>
    </>
  );
}
